import { clusterByPosition, formatClusterCount } from '@lib/media/commentAnchor';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckIcon from '@mui/icons-material/Check';
import FullscreenExitRoundedIcon from '@mui/icons-material/FullscreenExitRounded';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import VolumeDownRoundedIcon from '@mui/icons-material/VolumeDownRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import { Divider, IconButton, Menu, MenuItem, Slider, Tooltip } from '@mui/material';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import css from '../MomentMergeVideoPlayer.module.scss';
import { isAdjustable } from '../hooks/selectionSpan';
import { ChapterMark, CommentMark, PlaybackSource, SelectionSpan } from '../types';

/**
 * The quality menu's rows, in the order they are offered. `hint` is a tooltip rather than a
 * second line of text: menu rows are a fixed 36px in this app's theme (see `MuiMenuItem`), and
 * the trade-off between the two is worth a sentence somewhere.
 */
const PLAYBACK_SOURCES: { id: PlaybackSource; label: string; hint: string }[] = [
  { id: 'preview', label: 'Preview', hint: 'Fast to start, plays in any browser' },
  {
    id: 'original',
    label: 'Original',
    hint: 'Full quality — slower to start, and some formats will not play',
  },
];

// Min container widths at which each control remains visible.
// Widest (least essential) controls hide first as the container shrinks.
// Nothing here is a packing calculation — the progress bar is `flex: 1`, so the bar never
// overflows — these are a rank order. A control added without an entry outranks every control
// that has one, which is why the quality button has one: full quality is a deliberate detour,
// so it goes before play/pause and mute do.
const CONTROL_BREAKPOINTS = {
  timecodeButton: 500,
  qualityButton: 480,
  volumeSlider: 450,
  remainingTime: 400,
  playPause: 380,
  timeInput: 340,
  muteButton: 300,
} as const;

interface VideoControlsProps {
  isPlaying: boolean;
  volume: number;
  isMuted: boolean;
  isFullscreen: boolean;
  currentTime: number;
  totalDuration: number;
  timeInputValue: string | null;
  timeInputRef: React.RefObject<HTMLInputElement | null>;
  progressPercentage: number;
  showHoverTime: boolean;
  hoverTime: number;
  hoverPosition: number;
  progressBarHitboxRef: React.RefObject<HTMLDivElement | null>;
  onTogglePlayPause: () => void;
  onToggleMute: () => void;
  onVolumeChange: (event: Event, value: number | number[]) => void;
  onToggleFullscreen: () => void;
  onTimeInputChange: (value: string) => void;
  onTimeInputSubmit: () => void;
  onTimeInputFocus: () => void;
  onTimeInputBlur: () => void;
  onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
  onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
  onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
  onPointerCancel: () => void;
  onProgressLeave: () => void;
  onProgressEnter: () => void;
  containerWidth: number;
  formatTime: (seconds: number) => string;
  formatRemainingTime: (seconds: number) => string;
  getVolumeIcon: () => 'muted' | 'low' | 'high';
  playbackSource: PlaybackSource;
  /** Omit to hide the quality menu entirely — see `MomentMergeVideoPlayerProps`. */
  onPlaybackSourceChange?: (source: PlaybackSource) => void;
  playbackSourceLabels?: Partial<Record<PlaybackSource, string>>;
  /** Scene divisions to mark on the bar. Omitted for media that has none. */
  chapters?: ChapterMark[];
  commentMarks?: CommentMark[];
  selectedCommentId?: string | null;
  onCommentMarkClick?: (commentId: string) => void;
  /** The marked range to draw. Omitted when nothing is marked. */
  selection?: SelectionSpan;
  /**
   * Whether the range may be edited here, which is what puts handles on it — and only on a band
   * wide enough for this bar to resolve. See `isAdjustable`.
   */
  selectable?: boolean;
}

export const VideoControls: React.FC<VideoControlsProps> = ({
  isPlaying,
  volume,
  isMuted,
  isFullscreen,
  currentTime,
  totalDuration,
  timeInputValue,
  timeInputRef,
  progressPercentage,
  showHoverTime,
  hoverTime,
  hoverPosition,
  progressBarHitboxRef,
  onTogglePlayPause,
  onToggleMute,
  onVolumeChange,
  onToggleFullscreen,
  onTimeInputChange,
  onTimeInputSubmit,
  onTimeInputFocus,
  onTimeInputBlur,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  onProgressLeave,
  onProgressEnter,
  containerWidth,
  formatTime,
  formatRemainingTime,
  getVolumeIcon,
  playbackSource,
  onPlaybackSourceChange,
  playbackSourceLabels,
  chapters,
  commentMarks,
  selectedCommentId,
  onCommentMarkClick,
  selection,
  selectable,
}) => {
  const volumeIcon = getVolumeIcon();
  // Named on hover so scrubbing a 90-minute media says where you are landing, not just when.
  // The lookup stays over every chapter even when the tick for it was dropped below.
  const hoveredChapter =
    chapters?.find(
      (chapter) => hoverPosition >= chapter.startPercent && hoverPosition < chapter.endPercent,
    ) ?? null;

  /**
   * Divisions closer together than this are not drawn.
   *
   * A media with 150 scenes puts a tick every couple of pixels and the bar stops reading as a
   * progress bar at all — it becomes a hatch pattern with no information in it. Dropping the
   * crowded ones keeps the coarse structure legible; the hover tooltip still names every
   * chapter, so nothing is actually lost. 6px is about the narrowest gap that still reads as
   * two marks rather than a thick one at this bar's 8px height.
   */
  const minTickGapPercent = containerWidth > 0 ? (6 / containerWidth) * 100 : 0;

  /**
   * Built here rather than in the JSX below: `hoverPosition` comes off an unthrottled
   * `onMouseMove`, so scrubbing re-renders these controls at pointer-event rate, and the tick
   * list depends on neither the hover nor the playhead.
   */
  const chapterTicks = useMemo(() => {
    const ticks: React.ReactNode[] = [];
    let lastAt = 0;
    (chapters ?? []).forEach((chapter, i) => {
      // The first division sits on the bar's left cap, where a tick only thickens the edge.
      if (chapter.startPercent <= 0.5) return;
      if (chapter.startPercent - lastAt < minTickGapPercent) return;
      ticks.push(
        <span key={i} className={css.chapterTick} style={{ left: `${chapter.startPercent}%` }} />,
      );
      lastAt = chapter.startPercent;
    });
    return ticks;
  }, [chapters, minTickGapPercent]);

  const [qualityAnchor, setQualityAnchor] = useState<HTMLElement | null>(null);

  /** The drawn mark under the pointer or holding focus (its `key`), whose preview is showing. */
  const [previewedKey, setPreviewedKey] = useState<string | null>(null);

  /**
   * What is actually drawn: one mark per span, and one per group of frame dots that would land on
   * top of each other at this bar's width — see `clusterByPosition`. Several comments on one
   * frame used to be one dot with the rest hidden under it.
   *
   * Spans first and dot groups after, in DOM order as well as z-order, so a dot pinned to a span's
   * first frame is drawn over that span rather than buried in it — and so tabbing reaches a span
   * before the dots that sit on it.
   */
  const drawnCommentMarks = useMemo<DrawnCommentMark[]>(() => {
    const marks = commentMarks ?? [];
    const isPoint = (mark: CommentMark) => mark.endPercent <= mark.startPercent;
    const dotGapPercent = containerWidth > 0 ? (COMMENT_DOT_CLUSTER_PX / containerWidth) * 100 : 0;
    const dotGroups = clusterByPosition(
      marks.filter(isPoint).map((mark) => ({ mark, position: mark.startPercent })),
      dotGapPercent,
    );
    return [
      ...marks
        .filter((mark) => !isPoint(mark))
        .map((mark) => ({ key: mark.id, isPoint: false, members: [mark] })),
      ...dotGroups.map((group) => ({
        key: group[0].mark.id,
        isPoint: true,
        members: group.map(({ mark }) => mark),
      })),
    ];
  }, [commentMarks, containerWidth]);
  const previewed = drawnCommentMarks.find((drawn) => drawn.key === previewedKey);

  // Progressive hiding: controls disappear as container shrinks
  const showTimecodeButton = containerWidth >= CONTROL_BREAKPOINTS.timecodeButton;
  const isCompact = !showTimecodeButton;
  const showVolumeSlider = containerWidth >= CONTROL_BREAKPOINTS.volumeSlider;
  const showRemainingTime = containerWidth >= CONTROL_BREAKPOINTS.remainingTime;
  const showPlayPause = containerWidth >= CONTROL_BREAKPOINTS.playPause;
  const showTimeInput = containerWidth >= CONTROL_BREAKPOINTS.timeInput;
  const showMuteButton = containerWidth >= CONTROL_BREAKPOINTS.muteButton;
  const showQualityButton = containerWidth >= CONTROL_BREAKPOINTS.qualityButton;

  // The menu unmounts with its button, so an anchor left set would reopen it against a detached
  // node the next time the bar is wide enough. Leaving fullscreen with the menu open is the way
  // in: the container loses hundreds of pixels in one step.
  useEffect(() => {
    if (!showQualityButton) setQualityAnchor(null);
  }, [showQualityButton]);

  // Fullscreen is requested on the video container (see `toggleFullscreen`), and nothing outside
  // that element is painted while it is active, so every portalled popover here has to open
  // inside it or open invisibly. MUI portals a `Tooltip`'s Popper to `body` unless
  // `slotProps.popper.container` says otherwise. Read at render, which the `isFullscreen` prop
  // guarantees happens on every toggle.
  const fullscreenContainer = document.fullscreenElement ?? undefined;
  const popperInFullscreen = { popper: { container: fullscreenContainer } };

  const getVolumeIconComponent = () => {
    switch (volumeIcon) {
      case 'muted':
        return <VolumeOffRoundedIcon />;
      case 'low':
        return <VolumeDownRoundedIcon />;
      case 'high':
        return <VolumeUpRoundedIcon />;
      default:
        return <VolumeUpRoundedIcon />;
    }
  };

  return (
    <div
      className={classNames(css.customControls, { [css.compact]: isCompact })}
      onClick={(e) => e.stopPropagation()}
    >
      {showPlayPause && (
        <IconButton onClick={onTogglePlayPause}>
          {isPlaying ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
        </IconButton>
      )}

      {showMuteButton && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onToggleMute();
          }}
        >
          {getVolumeIconComponent()}
        </IconButton>
      )}

      {showVolumeSlider && (
        <div className={css.volumeSlider} onClick={(e) => e.stopPropagation()}>
          <Slider
            value={volume}
            onChange={onVolumeChange}
            aria-label="Volume"
            min={0}
            max={100}
            size="small"
          />
        </div>
      )}

      {showTimeInput && (
        <div className={css.timeDisplayLeft} onClick={(e) => e.stopPropagation()}>
          <input
            ref={timeInputRef}
            className={css.timeInput}
            value={
              timeInputValue !== null
                ? timeInputValue
                : totalDuration > 0
                  ? formatTime(currentTime)
                  : '0:00'
            }
            onChange={(e) => onTimeInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.stopPropagation();
                onTimeInputSubmit();
                timeInputRef.current?.blur();
              } else if (e.key === 'Escape') {
                e.stopPropagation();
                onTimeInputChange('');
                timeInputRef.current?.blur();
              }
            }}
            onFocus={onTimeInputFocus}
            onClick={(e) => e.stopPropagation()}
            onBlur={onTimeInputBlur}
            placeholder="HH:MM:SS"
            style={{
              width: `${(timeInputValue !== null ? (timeInputValue.length === 0 ? 'HH:MM:SS' : timeInputValue) : formatTime(currentTime)).length}ch`,
            }}
          />
        </div>
      )}

      <div className={css.progressContainer} onClick={(e) => e.stopPropagation()}>
        <div
          className={classNames(css.progressBarHitbox, { [css.progressBarSelectable]: selectable })}
          ref={progressBarHitboxRef}
          // The wrapper's own click toggles play/pause, so the press must not reach it. The
          // gesture itself is resolved in `useProgressBar` — this element deliberately has no
          // `onClick` seek any more, because a completed drag fires one too.
          onClick={(e) => e.stopPropagation()}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          onMouseLeave={onProgressLeave}
          onMouseEnter={onProgressEnter}
        >
          <div className={css.progressBar}>
            <div
              className={css.progress}
              style={{
                width: `${progressPercentage}%`,
              }}
            />
            {/* After the fill in DOM order so the divisions stay visible over the played
                portion. The bar clips its own overflow, so a chapter reaching past either end
                needs no clamping here. */}
            {chapterTicks}
            {/* Last, so the marked range reads as sitting on top of both the fill and the
                divisions — it is the thing the viewer is currently acting on. */}
            {selection && (
              <span
                className={css.selectionBand}
                style={{
                  left: `${selection.startPercent}%`,
                  width: `${Math.max(0, selection.endPercent - selection.startPercent)}%`,
                }}
              />
            )}
          </div>
        </div>

        {/* Outside the bar, beside the hover needle rather than inside the band — for the same
            reason that needle is out here: `.progressBar` clips its overflow, and a handle tall
            enough to see would be sliced back to the bar's 8px.

            Withheld on a band this bar cannot adjust — see `isAdjustable`. The band itself stays
            drawn above; it is the grabbable-looking part that would be lying. */}
        {selection && selectable && isAdjustable(selection) && (
          <>
            <span className={css.selectionHandle} style={{ left: `${selection.startPercent}%` }} />
            <span className={css.selectionHandle} style={{ left: `${selection.endPercent}%` }} />
          </>
        )}

        {/* Under the bar rather than on it, and outside `.progressBar` for the same reason the
            selection handles are: that element clips its overflow, and a mark tall enough to
            aim at would be sliced back to the bar's 8px.

            Each one stops its own press from reaching the hitbox, which would otherwise read it
            as a scrub and drag the playhead to wherever the mark happens to sit. */}
        {drawnCommentMarks.map((drawn) => {
          // The mark a group is drawn at and points to: its first comment, in timeline order —
          // the same one the Comments panel lists first among them.
          const [lead] = drawn.members;
          const count = drawn.members.length;
          const showPreview = () => setPreviewedKey(drawn.key);
          // Only clears its own preview, so moving straight from one mark to the next does not
          // race the next one's `enter` against this one's `leave`.
          const hidePreview = () =>
            setPreviewedKey((current) => (current === drawn.key ? null : current));
          return (
            <button
              key={drawn.key}
              type="button"
              aria-label={count > 1 ? `${count} comments here` : 'Go to comment'}
              aria-describedby={previewedKey === drawn.key ? PREVIEW_ID : undefined}
              className={classNames(css.commentMark, {
                [css.commentMarkPoint]: drawn.isPoint,
                [css.commentMarkCluster]: count > 1,
                [css.commentMarkSelected]: drawn.members.some((m) => m.id === selectedCommentId),
              })}
              style={
                {
                  left: `${lead.startPercent}%`,
                  // A span is drawn at its real width; `min-width` floors it so a two-second note
                  // on an hour-long media is still something you can hit. A dot's size is the
                  // class's, so it gets no width here.
                  width: drawn.isPoint ? undefined : `${lead.endPercent - lead.startPercent}%`,
                  '--lane': drawn.isPoint ? 0 : lead.lane,
                } as React.CSSProperties
              }
              onPointerDown={(e) => e.stopPropagation()}
              onPointerEnter={showPreview}
              onPointerLeave={hidePreview}
              onFocus={showPreview}
              onBlur={hidePreview}
              onClick={(e) => {
                e.stopPropagation();
                onCommentMarkClick?.(lead.id);
              }}
              // The wrapper's own click toggles play/pause, and a button fires its click from the
              // keyboard too — so this has to stop there as well, not only under the pointer.
              onKeyDown={(e) => e.stopPropagation()}
            >
              {count > 1 && formatClusterCount(count)}
            </button>
          );
        })}

        {previewed && <CommentPreview marks={previewed.members} />}

        {showHoverTime && totalDuration > 0 && (
          <div className={css.hoverTimeIndicator} style={{ left: `${hoverPosition}%` }}>
            <div className={css.hoverTimestamp}>
              {hoveredChapter && <span className={css.hoverChapter}>{hoveredChapter.title}</span>}
              {formatTime(hoverTime)}
            </div>
          </div>
        )}
      </div>

      {showRemainingTime && (
        <div className={css.timeDisplayRight} onClick={(e) => e.stopPropagation()}>
          {totalDuration > 0 ? formatRemainingTime(currentTime) : '-0:00'}
        </div>
      )}

      {showTimecodeButton && (
        <Tooltip title="Scrub to timecode" placement="top" arrow slotProps={popperInFullscreen}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              timeInputRef.current?.focus();
            }}
          >
            <AccessTimeIcon />
          </IconButton>
        </Tooltip>
      )}

      {onPlaybackSourceChange && showQualityButton && (
        <>
          <Tooltip title="Playback quality" placement="top" arrow slotProps={popperInFullscreen}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setQualityAnchor(e.currentTarget);
              }}
            >
              <TuneRoundedIcon />
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={qualityAnchor}
            open={!!qualityAnchor}
            onClose={() => setQualityAnchor(null)}
            container={fullscreenContainer}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem className={css.qualityMenuHeader} disableRipple>
              Quality
            </MenuItem>
            <Divider />
            {PLAYBACK_SOURCES.map(({ id, label, hint }) => (
              <Tooltip key={id} title={hint} placement="right" arrow slotProps={popperInFullscreen}>
                <MenuItem
                  className={css.qualityMenuRow}
                  selected={id === playbackSource}
                  onClick={() => {
                    setQualityAnchor(null);
                    onPlaybackSourceChange(id);
                  }}
                >
                  <span>{label}</span>
                  <span className={css.qualityMenuTrailing}>
                    {playbackSourceLabels?.[id]}
                    {id === playbackSource && (
                      <CheckIcon className={css.qualityCheckIcon} fontSize="small" />
                    )}
                  </span>
                </MenuItem>
              </Tooltip>
            ))}
          </Menu>
        </>
      )}

      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          onToggleFullscreen();
        }}
      >
        {isFullscreen ? <FullscreenExitRoundedIcon /> : <FullscreenRoundedIcon />}
      </IconButton>
    </div>
  );
};

/** One thing drawn under the bar: a span, or a group of frame dots that share a spot. */
type DrawnCommentMark = { key: string; isPoint: boolean; members: CommentMark[] };

/**
 * How close two frame dots can be, in px, before they are drawn as one mark with a count. The
 * dot is 8px with a 1.5px ring either side, so under this they visibly overlap.
 */
const COMMENT_DOT_CLUSTER_PX = 11;

/** How many comments a group's preview lists before summing up the rest. */
const PREVIEW_MAX_ENTRIES = 3;

const PREVIEW_ID = 'scrub-bar-comment-preview';

/**
 * How near an edge of the bar, as a percentage of it, a preview stops centring on its mark and
 * aligns to that edge instead. A preview is up to 260px wide on a bar of roughly 500, so centring
 * one on a mark in the outer 15% would put a good part of it past the player's edge.
 */
const PREVIEW_EDGE_PERCENT = 15;

/**
 * Who wrote a comment and what it says, above the bar while its mark is hovered or focused — so
 * the marks can be read where they are, without opening the Comments panel to find out what
 * each one is. For a group of dots on one spot it lists them, which is the only place the ones
 * after the first can be read from the bar at all.
 *
 * Positioned over the middle of the mark, which for a long span is a better answer than either
 * end: the reader is pointing somewhere inside it.
 */
const CommentPreview = ({ marks }: { marks: CommentMark[] }) => {
  const [lead] = marks;
  const middle = (lead.startPercent + Math.max(lead.startPercent, lead.endPercent)) / 2;
  const shift =
    middle < PREVIEW_EDGE_PERCENT ? '0%' : middle > 100 - PREVIEW_EDGE_PERCENT ? '-100%' : '-50%';
  const shown = marks.slice(0, PREVIEW_MAX_ENTRIES);
  const hidden = marks.length - shown.length;
  return (
    <div
      id={PREVIEW_ID}
      role="tooltip"
      className={classNames(css.commentPreview, { [css.commentPreviewGroup]: marks.length > 1 })}
      style={{ left: `${middle}%`, transform: `translateX(${shift})` }}
    >
      {shown.map((mark) => (
        <div key={mark.id} className={css.commentPreviewEntry}>
          <span className={css.commentPreviewMeta}>
            <span className={css.commentPreviewAuthor}>{mark.preview.author}</span> ·{' '}
            {mark.preview.label}
          </span>
          <span className={css.commentPreviewText}>{mark.preview.text}</span>
        </div>
      ))}
      {hidden > 0 && (
        <span className={css.commentPreviewMore}>
          and {hidden} more — open Comments to read {hidden === 1 ? 'it' : 'them'}
        </span>
      )}
    </div>
  );
};
